import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Breadcrumbs,
  Button,
  EmptyState,
  Search,
} from '@enamelai/design-system'
import { Icon as CheckmarkIcon } from '@enamelai/design-system/icons/Checkmark2'
import { Icon as ClockIcon } from '@enamelai/design-system/icons/Clock'
import { SchemaFormRenderer } from '@components/pages/FormBuilder/Shared/SchemaFormRenderer'
import type { GroupField } from '@components/pages/FormBuilder/types'
import type { SchemaFormFieldValues } from '@components/pages/FormBuilder/Shared/SchemaFormRenderer'
import {
  mergePatientIntoForms,
  type FormData,
} from '@components/pages/FormBuilder/PendingFormsTable/CompletedFormsModal.component'
import {
  formDataToSchemaEntry,
  schemaJsonToGroups,
} from '@components/pages/FormBuilder/SendingFormsTable/formTemplateToFormData'
import { getFormTemplateById } from '@services/api/forms.api'
import { generateFormPDF } from '@utils/formPdfGenerator.util'
import { useAppSelector } from '@store/hooks'
import { buildClinicPdfHeaderPayload } from '@utils/clinic.util'
import { FormPdfCapture } from '@components/pages/FormBuilder/Shared/FormPdfCapture'
import './CompletedFormsView.css'

export interface IncompleteFormEntry {
  id: string
  assignmentId?: string
  name: string
}

export interface CompletedFormsViewLocationState {
  patientName: string
  appointmentType: string
  roomNo: string
  completedForms: FormData[]
  incompleteForms?: IncompleteFormEntry[]
  patientMobile?: string
  patientEmail?: string
}

interface CompletedFormsViewProps {
  payload?: CompletedFormsViewLocationState
  onBackToDashboard?: (options?: { tab?: 'pending' | 'completed' }) => void
  onCompleteForm?: (formTitle?: string) => void
}

interface IncompleteFormPreview {
  groups: GroupField[]
  fieldValues: SchemaFormFieldValues
}

const CompletedFormsView: React.FC<CompletedFormsViewProps> = ({
  payload,
  onBackToDashboard,
  onCompleteForm,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const routedState = (location.state ||
    {}) as Partial<CompletedFormsViewLocationState>
  const state: Partial<CompletedFormsViewLocationState> = payload ?? routedState

  const patientName = state.patientName ?? 'Patient'
  const appointmentType = state.appointmentType ?? 'Consultation'
  const roomNo = state.roomNo ?? ''
  const completedForms = useMemo(
    () => state.completedForms ?? [],
    [state.completedForms],
  )
  const incompleteForms = useMemo(
    () => state.incompleteForms ?? [],
    [state.incompleteForms],
  )
  const patientMobile = state.patientMobile
  const patientEmail = state.patientEmail

  const completedWithPatient = useMemo(
    () =>
      mergePatientIntoForms(completedForms, {
        patientName,
        patientMobile,
        patientEmail,
      }),
    [completedForms, patientName, patientMobile, patientEmail],
  )

  type ListFormItem =
    | {
        id: string
        name: string
        completed: true
        form: FormData
        dateCompleted?: string
      }
    | { id: string; name: string; completed: false }

  const allFormsList = useMemo<ListFormItem[]>(() => {
    const completed: ListFormItem[] = completedWithPatient.map((form) => ({
      id: form.assignmentId ?? form.id,
      name: form.name,
      dateCompleted: form.dateCompleted,
      completed: true as const,
      form,
    }))
    const incomplete: ListFormItem[] = incompleteForms.map((f) => ({
      id: f.assignmentId ?? f.id,
      name: f.name,
      completed: false as const,
    }))
    return [...completed, ...incomplete]
  }, [completedWithPatient, incompleteForms])

  const initialSelectedId = allFormsList.length > 0 ? allFormsList[0]!.id : null
  const [selectedFormId, setSelectedFormId] = useState<string | null>(
    initialSelectedId,
  )
  const [sidebarSearchQuery, setSidebarSearchQuery] = useState('')
  const [incompleteFormPreviewMap, setIncompleteFormPreviewMap] = useState<
    Record<string, IncompleteFormPreview>
  >({})
  const pdfDownloadContainerRef = useRef<HTMLDivElement>(null)
  const selectedClinic = useAppSelector(
    (appState) => appState.clinic.selectedClinic,
  )
  const clinicPdfHeader = useMemo(
    () => buildClinicPdfHeaderPayload(selectedClinic),
    [selectedClinic],
  )

  useEffect((): (() => void) => {
    let isActive = true
    Promise.all(
      incompleteForms.map(async (form) => {
        const formTemplate = await getFormTemplateById(form.id).catch(
          (): null => null,
        )
        return {
          id: form.assignmentId ?? form.id,
          preview: {
            groups: schemaJsonToGroups(formTemplate?.schema_json),
            fieldValues: {},
          },
        }
      }),
    ).then((entries) => {
      if (!isActive) return
      const previewMap: Record<string, IncompleteFormPreview> = {}
      entries.forEach((entry) => {
        previewMap[entry.id] = entry.preview
      })
      setIncompleteFormPreviewMap(previewMap)
    })
    return (): void => {
      isActive = false
    }
  }, [incompleteForms])

  const filteredForms = useMemo(() => {
    if (!sidebarSearchQuery.trim()) return allFormsList
    const q = sidebarSearchQuery.toLowerCase()
    return allFormsList.filter((f) => f.name.toLowerCase().includes(q))
  }, [allFormsList, sidebarSearchQuery])

  const selectedItem = allFormsList.find((f) => f.id === selectedFormId) ?? null
  const selectedForm =
    selectedItem?.completed === true ? selectedItem.form : null
  const selectedIncompletePreview =
    useMemo((): IncompleteFormPreview | null => {
      if (selectedItem?.completed !== false) return null
      return (
        incompleteFormPreviewMap[selectedItem.id] ?? {
          groups: [],
          fieldValues: {},
        }
      )
    }, [incompleteFormPreviewMap, selectedItem])
  const selectedSchemaEntry = useMemo(() => {
    if (selectedForm) return formDataToSchemaEntry(selectedForm)
    if (selectedIncompletePreview) return selectedIncompletePreview
    return null
  }, [selectedForm, selectedIncompletePreview])

  const handleBack = useCallback(() => {
    if (onBackToDashboard) {
      onBackToDashboard({ tab: 'pending' })
      return
    }
    navigate('/form-builder', { state: { tab: 'pending' } })
  }, [navigate, onBackToDashboard])

  const handleCompleteForm = useCallback(() => {
    if (onCompleteForm) {
      onCompleteForm(selectedItem?.name)
      return
    }
    if (selectedItem?.name) {
      navigate('/form-builder/create', {
        state: { formTitle: selectedItem.name },
      })
    } else {
      navigate('/form-builder/create')
    }
  }, [navigate, onCompleteForm, selectedItem?.name])

  const handleDownloadPdf = useCallback(() => {
    const el = pdfDownloadContainerRef.current
    if (!el || !selectedForm) return
    const filename = `${patientName.replace(/\s+/g, '-')}-${selectedForm.name.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`
    generateFormPDF({
      element: el,
      filename,
      title: selectedForm.name,
      clinicPdfHeader,
    })
  }, [selectedForm, patientName, clinicPdfHeader])

  const roomLabel = roomNo
    ? roomNo.trim().toLowerCase().startsWith('room')
      ? roomNo.trim()
      : `Room ${roomNo.trim()}`
    : ''
  const appointmentLabel = roomLabel
    ? `${appointmentType} - ${roomLabel}`
    : appointmentType

  const breadcrumbItems = [
    { id: 'patient', label: patientName, onClick: handleBack },
    { id: 'pending-forms', label: 'Pending forms', onClick: handleBack },
    { id: 'appointment', label: appointmentLabel },
  ]

  const breadcrumbs = (
    <Breadcrumbs
      className="cfv-breadcrumbs"
      items={breadcrumbItems}
      showBackIcon
      onBackClick={handleBack}
    />
  )

  if (allFormsList.length === 0) {
    return (
      <div className="cfv-container-fluid">
        <div className="cfv-layout">
          <div className="cfv-container">
            <div className="cfv-outer">
              <header className="cfv-top-row">{breadcrumbs}</header>
              <EmptyState
                className="cfv-empty"
                title="No forms"
                description="No form data was passed. Go back to Pending Forms and open a row with forms."
                outlined
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="cfv-container-fluid">
      <div className="cfv-layout">
        <aside className="cfv-sidebar" aria-label="Forms">
          <div className="cfv-sidebar-title">Forms</div>
          <Search
            placeholder="Search"
            ariaLabel="Search forms"
            value={sidebarSearchQuery}
            onChange={setSidebarSearchQuery}
            onClear={() => setSidebarSearchQuery('')}
          />
          <div className="cfv-sidebar-list">
            {filteredForms.map((item) => {
              const isActive = selectedFormId === item.id
              const StatusIcon = item.completed ? CheckmarkIcon : ClockIcon
              return (
                <button
                  type="button"
                  key={item.id}
                  className={`cfv-sidebar-item${isActive ? ' cfv-sidebar-item--active' : ''}`}
                  aria-current={isActive ? 'true' : undefined}
                  title={
                    item.completed && item.dateCompleted
                      ? `${item.name} (${item.dateCompleted})`
                      : item.name
                  }
                  onClick={() => setSelectedFormId(item.id)}
                >
                  <span className="cfv-sidebar-item-label">{item.name}</span>
                  <StatusIcon
                    className={`cfv-sidebar-item-status ${item.completed ? 'cfv-status-completed' : 'cfv-status-incomplete'}`}
                    aria-label={item.completed ? 'Completed' : 'Incomplete'}
                  />
                </button>
              )
            })}
          </div>
        </aside>
        <div className="cfv-container">
          <div className="cfv-outer">
            <header className="cfv-top-row">
              {breadcrumbs}
              <div className="cfv-top-row-right">
                <Button
                  variant="primary"
                  size="md"
                  type="button"
                  onClick={handleDownloadPdf}
                  disabled={!selectedForm}
                  title={
                    !selectedForm ? 'Complete the form to download' : undefined
                  }
                >
                  Download as PDF
                </Button>
              </div>
            </header>
            <div className="cfv-inner">
              <main className="cfv-main">
                <div
                  className={
                    selectedItem && !selectedForm
                      ? 'cfv-main-inner cfv-main-inner-centered'
                      : 'cfv-main-inner'
                  }
                >
                  {selectedSchemaEntry ? (
                    <div className="cfv-schema-wrapper">
                      <SchemaFormRenderer
                        groups={selectedSchemaEntry.groups}
                        formTitle={
                          selectedForm?.name ?? selectedItem?.name ?? ''
                        }
                        fieldValues={selectedSchemaEntry.fieldValues}
                        readOnly={true}
                        richTextReadOnly={true}
                        patientName={
                          selectedItem?.completed ? patientName : undefined
                        }
                        readOnlyDisplayMode={
                          selectedItem?.completed ? 'print' : 'default'
                        }
                      />
                    </div>
                  ) : selectedItem && selectedItem.completed === false ? (
                    <EmptyState
                      className="cfv-incomplete-empty-state"
                      title={selectedItem.name}
                      description="This form has not been completed yet."
                      actionLabel="Complete form"
                      onAction={handleCompleteForm}
                    />
                  ) : null}
                </div>
              </main>
            </div>
          </div>
        </div>
      </div>{' '}
      {selectedSchemaEntry && selectedForm && (
        <div
          ref={pdfDownloadContainerRef}
          style={{
            position: 'fixed',
            left: '-9999px',
            top: 0,
            width: '794px',
            zIndex: -1,
            pointerEvents: 'none',
          }}
          className="form-preview-container"
          aria-hidden="true"
        >
          <FormPdfCapture
            groups={selectedSchemaEntry.groups}
            formTitle={selectedForm.name}
            fieldValues={selectedSchemaEntry.fieldValues}
            patientName={patientName}
            readOnlyDisplayMode="print"
          />
        </div>
      )}
    </div>
  )
}

export default CompletedFormsView
